import { createContext, useContext } from 'react';
import type { JSX, ReactNode } from 'react';
import type { EnfantAEnregistrer } from './templates.js';

/**
 * LES PLANS RECURRENTS, cote SOCLE : le PORT, et les raisons qui eteignent le
 * geste.
 *
 * MEME PATRON QUE LES MODELES (`lib/templates.tsx`), et pour la meme raison :
 * `@taki/ui` n'importe jamais `@taki/sdk` (ordre de build sdk -> ui -> shell ->
 * apps). Le socle decrit la FORME du service, l'HOTE la remplit avec
 * `sdk.recurrences` et monte `RecurrencesProvider` a cote de `ModelesProvider`.
 *
 * UN SEUL GESTE S'EN SERT, « Créer un plan récurrent » (chantier
 * `plan-sur-modele`), dans le menu « ... » de deux fiches (`views/useScreen.ts`) :
 *  - sur une FICHE : le SDK en tire un MODELE, puis le plan qui le cite
 *    (`fromRecord`) ;
 *  - sur un MODELE ouvert en mode Modeles : le plan seul, qui cite ce modele
 *    (`fromTemplate`).
 * UN PLAN NE PORTE QUE SON RYTHME : le contenu des fiches qu'il fait naitre vit
 * dans le modele qu'il cite, et se corrige la, en mode Modeles. Il n'y a donc
 * plus rien a « appliquer » a un plan depuis une fiche.
 *
 * CE FICHIER NE CALCULE AUCUNE OCCURRENCE, et le SDK non plus : le moteur vit
 * sur le serveur (`functions/src/recurrence/moteur.ts`), et nulle part ailleurs.
 */

/** Une fiche liee a recreer a chaque occurrence : la meme forme que pour un modele. */
export type EnfantDePlan = EnfantAEnregistrer;

/**
 * LE CHEMIN DU LIEN D'UNE FICHE VERS LE PLAN QUI L'A CREEE. Le SDK le pose
 * (`CLEF_LIEN_PLAN = 'recurrenceId'`, `packages/sdk/src/recurrences.ts`) et le
 * socle ne peut pas l'importer : la valeur est donc RECOPIEE ici, et un test
 * d'`@taki/app-runtime` (qui voit les deux paquets) verifie qu'elles
 * concordent.
 */
export const CHEMIN_LIEN_PLAN = 'customFields.recurrenceId';

/** La famille des plans : la porte qui la montre est celle ou le plan neuf s'ouvre. */
export const FAMILLE_DES_PLANS = 'recurrences';

/**
 * LE SERVICE DES PLANS, tel que le socle l'attend.
 *
 * `record` PART TEL QUEL, champs standard compris : c'est le SDK qui sait ce
 * qui ne se recopie pas (l'id, les dates, l'auteur, le lien vers le plan). Le
 * socle n'y retire rien de plus.
 */
export interface RecurrencesPort {
  /**
   * LE NOM PROPOSE pour un plan tire d'une fiche : son titre SANS SA PERIODE
   * (« ..., septembre 2026 » retire). Facultatif : sans lui, le titre tel quel.
   * C'est l'hote qui le fournit, parce que la regle vit dans `@taki/data-model`
   * et que `@taki/ui` n'en depend pas.
   */
  nomPropose?(titre: string): string;
  /**
   * CREE UN MODELE DEPUIS LA FICHE OUVERTE, PUIS LE PLAN QUI LE CITE, et rend
   * l'id du PLAN : le socle l'ouvre ensuite. Le modele porte le nom du plan
   * (une seule saisie).
   */
  fromRecord(args: {
    /** Le repository du SDK de la fiche (`projects`) : le `type` du plan. */
    family: string;
    /** Le sous-type de la porte, `''` si elle n'en declare pas. */
    type: string;
    record: Record<string, unknown>;
    /** Le nom du plan, saisi par la personne. */
    name: string;
    /** Les fiches liees, lues par le socle. Absent : aucune lecture n'a eu lieu. */
    children?: EnfantDePlan[];
  }): Promise<{ id: string }>;
  /**
   * CREE UN PLAN QUI CITE UN MODELE EXISTANT, et rend son id. Rien n'est copie :
   * le plan relira ce modele a chaque passage.
   */
  fromTemplate(args: {
    /** L'id du modele ouvert en mode Modeles. */
    templateId: string;
    /** Le nom du plan, saisi par la personne. */
    name: string;
  }): Promise<{ id: string }>;
}

/**
 * LE CONTEXTE, DEFAUT `null`, et ce defaut est LU : un port absent eteint le
 * geste avec sa raison, il ne casse rien.
 */
const RecurrencesContext = createContext<RecurrencesPort | null>(null);

/** Fournisseur pose par l'HOTE (celui qui a le SDK), autour du shell. */
export function RecurrencesProvider({
  value,
  children,
}: {
  value: RecurrencesPort | null;
  children: ReactNode;
}): JSX.Element {
  return <RecurrencesContext.Provider value={value}>{children}</RecurrencesContext.Provider>;
}

/** Le port des plans, ou `null` quand l'hote n'en fournit pas. */
export function useRecurrencesPort(): RecurrencesPort | null {
  return useContext(RecurrencesContext);
}

/**
 * LES TROIS RAISONS QUI ETEIGNENT LES PLANS, dans l'ordre des modeles
 * (`RAISONS_MODELES`) et pour la meme raison : du plus general (le service) au
 * plus local (l'accord de la porte), donc de ce que le lecteur ne peut pas
 * changer vers ce qu'il change en une ligne.
 */
export const RAISONS_RECURRENCES = {
  /** Personne n'a monte de `RecurrencesProvider` au-dessus de l'ecran. */
  sansPort: "L'hôte ne fournit pas les plans récurrents.",
  /** La porte ne declare pas son jeu : on ne sait pas quelles fiches le plan creerait. */
  sansJeu: 'Cette app ne déclare pas le jeu de la porte.',
  /* Le service et l'adresse sont la, mais la porte n'a pas allume les plans.
     La phrase dit OU ca se regle pour qui la lit (le Studio), pas la clef de
     fichier : c'est une personne de l'org qui survole le geste grise. */
  sansActivation:
    'Les plans récurrents ne sont pas activés sur cette porte : ça se règle dans le Studio.',
} as const;

/**
 * LES PLANS SONT-ILS DISPONIBLES SUR CETTE PORTE, ET SINON POURQUOI ? La
 * PREMIERE chose qui manque, ou `null`. Fonction PURE : un test tient l'ordre.
 */
export function raisonDesRecurrences(etat: {
  port: RecurrencesPort | null;
  jeu: { family: string; type?: string } | null;
  recurrences: boolean;
}): string | null {
  if (etat.port == null) return RAISONS_RECURRENCES.sansPort;
  if (etat.jeu == null || etat.jeu.family === '') return RAISONS_RECURRENCES.sansJeu;
  if (!etat.recurrences) return RAISONS_RECURRENCES.sansActivation;
  return null;
}
